import { Phone, FileText, ShieldCheck, Zap, ChefHat, MapPin, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { SITE, HERO_IMAGES } from "@/data/siteData";

const BADGES = [
  { label: "Intervention Rapide", Icon: Zap },
  { label: "Hygiène & Sécurité", Icon: ShieldCheck },
  { label: "Restaurants & Cuisines Pro", Icon: ChefHat },
  { label: "Seine-et-Marne", Icon: MapPin },
];

export default function Hero({ title, subtitle, eyebrow }) {
  const h1 = title || (
    <>
      Dégraissage de <span className="dh-underline">Hotte</span> à Melun
      <span className="block text-[#066ec4] text-3xl sm:text-4xl lg:text-5xl mt-3 font-bold">
        Restaurants & Cuisines Professionnelles
      </span>
    </>
  );
  const sub = subtitle ||
    "Nettoyage de hottes professionnelles, extraction cuisine, gaines de ventilation et nettoyage cuisine professionnelle en Seine-et-Marne. Attestation officielle remise après chaque intervention.";

  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-white">
      <div className="absolute inset-0 dh-radial-glow pointer-events-none" />
      <div className="absolute inset-0 dh-dot-grid opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          {eyebrow && (
            <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-full bg-[#f1f5fb] text-[#00367e] text-sm font-semibold uppercase tracking-wider">
              <span className="w-2 h-2 rounded-full bg-[#066ec4] dh-pulse" />
              {eyebrow}
            </div>
          )}

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#00367e]">
            {h1}
          </h1>

          <p className="mt-6 text-lg md:text-xl text-slate-700 max-w-2xl leading-relaxed">
            {sub}
          </p>

          {/* Availability badge (no fake Google reviews) */}
          <div className="mt-6 inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-white border border-[#e2e8f0] shadow-[0_4px_14px_rgba(0,54,126,0.06)]" data-testid="hero-availability">
            <span className="relative flex items-center justify-center">
              <span className="absolute w-2.5 h-2.5 rounded-full bg-[#22c55e] dh-pulse" aria-hidden="true" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#22c55e]" />
            </span>
            <span className="font-semibold text-[#00367e] text-sm">Disponible {SITE.hoursLabel}</span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-slate-500 text-sm">
              <Clock size={14} /> Réponse rapide
            </span>
          </div>

          {/* CTAs */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={`tel:${SITE.phoneTel}`}
              data-testid="hero-call-now-btn"
              className="dh-cta-shine inline-flex items-center justify-center gap-3 px-7 py-5 rounded-full bg-[#00367e] hover:bg-[#066ec4] text-white font-extrabold text-base sm:text-lg shadow-xl transition-all"
            >
              <Phone size={22} strokeWidth={2.5} />
              APPELER MAINTENANT
            </a>
            <a
              href="#devis"
              data-testid="hero-quote-btn"
              className="inline-flex items-center justify-center gap-3 px-7 py-5 rounded-full bg-white border-2 border-[#00367e] text-[#00367e] hover:bg-[#00367e] hover:text-white font-extrabold text-base sm:text-lg transition-all"
            >
              <FileText size={22} strokeWidth={2.5} />
              DEMANDER UN DEVIS
            </a>
          </div>

          {/* Trust badges */}
          <div className="mt-10 flex flex-wrap gap-2.5" data-testid="hero-badges">
            {BADGES.map(({ label, Icon }) => (
              <div
                key={label}
                className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-[#e2e8f0] shadow-[0_4px_14px_rgba(0,54,126,0.06)] text-[13.5px] font-semibold text-[#00367e]"
              >
                <Icon size={15} className="text-[#066ec4]" /> {label}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Visual composite */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative">
            <img
              src={HERO_IMAGES.kitchen}
              alt="Cuisine de restaurant professionnelle haut de gamme à Melun"
              loading="eager"
              width="900" height="1100"
              className="rounded-3xl shadow-[0_30px_60px_rgba(0,54,126,0.18)] w-full object-cover aspect-[4/5]"
            />
            <img
              src={HERO_IMAGES.van}
              alt="Camion intervention Dégraissage Hotte Melun en Seine-et-Marne"
              loading="eager"
              width="500" height="350"
              className="absolute -bottom-8 -left-6 sm:-left-10 w-44 sm:w-60 rounded-2xl shadow-[0_20px_40px_rgba(0,54,126,0.25)] border-4 border-white"
            />
            <div className="absolute -top-5 -right-3 bg-[#00367e] text-white px-4 py-3 rounded-2xl shadow-xl">
              <div className="text-[11px] uppercase tracking-widest opacity-80">Disponible</div>
              <div className="font-extrabold text-lg flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#22c55e] dh-pulse" /> 7j/7 · 24h/24
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
